import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ExternalLink, Eye, EyeOff, Save, Download } from "lucide-react";

const PLANS = ["Secure", "Secure Plus", "Secure Pro", "Secure Max"];
const STATUSES = ["Not enrolled", "Active", "Paused", "Cancelled"];

export default function ClientMonitoring({ clientId }) {
  const [affiliate, setAffiliate] = useState("");
  const [m, setM] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/settings").then((r) => setAffiliate(r.data.idiq_affiliate_link || "")).catch(() => {});
    api.get(`/clients/${clientId}/monitoring`).then((r) => setM(r.data)).catch(() => {});
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setM((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/monitoring`, {
        idiq_username: m.idiq_username || "", idiq_password: m.idiq_password || "", ssn_last4: m.ssn_last4 || "",
        plan_type: m.plan_type || "Secure Pro", monthly_fee: m.monthly_fee || "", next_pull_date: m.next_pull_date || "",
        status: m.status || "Not enrolled", enrolled: !!m.enrolled,
      });
      toast.success("Monitoring saved");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };

  if (!m) return <p className="text-muted-foreground">Loading…</p>;

  const enrollLink = affiliate || "https://www.identityiq.com/";

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-primary/15 via-card/60 to-transparent border-primary/30" data-testid="monitoring-enroll-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-lg bg-primary/20 flex items-center justify-center shrink-0"><ShieldCheck className="h-6 w-6 text-primary" /></div>
            <div>
              <h3 className="font-display text-xl font-semibold">IdentityIQ Credit Monitoring</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">Enroll this client on a 3-bureau plan (Secure Pro / Max), then store their login here to pull reports into the AI scanner.</p>
              {!affiliate && <p className="text-xs text-amber-300 mt-2">Tip: add your affiliate link in Settings to earn commissions on enrollments.</p>}
            </div>
          </div>
          <a href={enrollLink} target="_blank" rel="noopener noreferrer" data-testid="idiq-enroll-link" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors">
            Enroll on IdentityIQ <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border" data-testid="monitoring-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold">Monitoring Account</h3>
          <Badge className={m.enrolled ? "bg-emerald-500/20 text-emerald-300" : "bg-secondary text-muted-foreground"}>{m.status}</Badge>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>IdentityIQ Username / Email</Label><Input value={m.idiq_username || ""} onChange={(e) => set("idiq_username", e.target.value)} data-testid="idiq-username" /></div>
          <div className="space-y-2">
            <Label>IdentityIQ Password</Label>
            <div className="relative">
              <Input type={showPw ? "text" : "password"} value={m.idiq_password || ""} onChange={(e) => set("idiq_password", e.target.value)} className="pr-10" data-testid="idiq-password" />
              <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="toggle-pw">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            </div>
          </div>
          <div className="space-y-2"><Label>SSN (last 4)</Label><Input maxLength={4} value={m.ssn_last4 || ""} onChange={(e) => set("ssn_last4", e.target.value)} data-testid="idiq-ssn" /></div>
          <div className="space-y-2">
            <Label>Plan Type</Label>
            <select value={m.plan_type || "Secure Pro"} onChange={(e) => set("plan_type", e.target.value)} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm" data-testid="idiq-plan">
              {PLANS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="space-y-2"><Label>Monthly Fee</Label><Input value={m.monthly_fee || ""} onChange={(e) => set("monthly_fee", e.target.value)} placeholder="$29.99" data-testid="idiq-fee" /></div>
          <div className="space-y-2"><Label>Next Report Pull</Label><Input type="date" value={m.next_pull_date || ""} onChange={(e) => set("next_pull_date", e.target.value)} data-testid="idiq-next-pull" /></div>
          <div className="space-y-2">
            <Label>Status</Label>
            <select value={m.status || "Not enrolled"} onChange={(e) => set("status", e.target.value)} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm" data-testid="idiq-status">
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 pt-7">
            <input type="checkbox" id="enrolled" checked={!!m.enrolled} onChange={(e) => set("enrolled", e.target.checked)} className="h-4 w-4 accent-blue-500" data-testid="idiq-enrolled" />
            <Label htmlFor="enrolled" className="cursor-pointer">Client is enrolled</Label>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-monitoring-btn">{saving ? "Saving…" : <><Save className="h-4 w-4 mr-1" /> Save</>}</Button>
          <a href="https://www.identityiq.com/" target="_blank" rel="noopener noreferrer" data-testid="idiq-open-portal">
            <Button variant="secondary" className="rounded-lg">Open IdentityIQ Login <ExternalLink className="h-4 w-4 ml-1" /></Button>
          </a>
        </div>
      </Card>

      <Card className="p-5 bg-card/40 border-border" data-testid="monitoring-pull-help">
        <h4 className="font-display font-semibold mb-2 flex items-center gap-2"><Download className="h-4 w-4 text-primary" /> Pull &amp; Scan a Report</h4>
        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
          <li>Open the IdentityIQ login and sign in with the credentials above.</li>
          <li>Download or screenshot the 3-bureau report.</li>
          <li>Go to the <span className="text-white font-medium">Negative Items &amp; Disputes</span> tab and use <span className="text-white font-medium">Upload Credit Report</span> — the AI reads it and drafts all three letters.</li>
        </ol>
      </Card>
    </div>
  );
}
